import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';

const FeedbackContext = createContext(null);
const TOAST_MS = 4000;

// Provides toast notifications and promise-based confirm dialogs, replacing
// window.alert / window.confirm (which look out of place and block the page).
export function FeedbackProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const nextId = useRef(0);

  const notify = useCallback((type, message) => {
    const id = ++nextId.current;
    setToasts(current => [...current, { id, type, message }]);
    setTimeout(() => setToasts(current => current.filter(t => t.id !== id)), TOAST_MS);
  }, []);

  const toast = useMemo(() => ({
    success: message => notify('success', message),
    error: message => notify('error', message),
  }), [notify]);

  // const ok = await confirm({ title, message, confirmLabel, danger })
  const confirm = useCallback(options => new Promise(resolve => {
    setConfirmState({ ...options, resolve });
  }), []);

  const closeConfirm = (result) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };

  const value = useMemo(() => ({ toast, confirm }), [toast, confirm]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}

      <div className="toast-region" role="status" aria-live="polite">
        {toasts.map(t => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            {t.type === 'success'
              ? <CheckCircle2 size={18} aria-hidden="true" />
              : <AlertCircle size={18} aria-hidden="true" />}
            {t.message}
          </div>
        ))}
      </div>

      <Modal
        open={!!confirmState}
        onClose={() => closeConfirm(false)}
        title={confirmState?.title || 'Are you sure?'}
        footer={
          <>
            <Button variant="secondary" onClick={() => closeConfirm(false)}>
              {confirmState?.cancelLabel || 'Cancel'}
            </Button>
            <Button
              variant={confirmState?.danger ? 'danger' : 'primary'}
              onClick={() => closeConfirm(true)}
              data-autofocus
            >
              {confirmState?.confirmLabel || 'Confirm'}
            </Button>
          </>
        }
      >
        {confirmState?.message && <p className="muted">{confirmState.message}</p>}
      </Modal>
    </FeedbackContext.Provider>
  );
}

export function useToast() {
  return useContext(FeedbackContext).toast;
}

export function useConfirm() {
  return useContext(FeedbackContext).confirm;
}
